/**
 * PortalInvites — the staff-facing list of portal invites for one student,
 * with a Revoke button on the ones that can still be used.
 *
 * WHY THIS EXISTS. A guardian invite grants FAMILY-WIDE read to whoever holds
 * the link — schedule, students, lesson recaps — for its full 7 days. Revoke
 * shipped as a tested callable in slice P1 and was never wired to anything, so
 * a mis-sent link could only be killed with the Admin SDK. This is the button.
 *
 * IT ALSO ANSWERS THE QUESTION STAFF ACTUALLY ASK, which is not "revoke this"
 * but "was this family ever invited, and did they ever claim it?" There was no
 * surface for that at all. The same list answers both, which is why it renders
 * for every staff member and only the ACTION is permission-gated.
 *
 * Read access is `isEmployeeOrAdmin()`; the revoke callable is gated on
 * `sendEmail`. The button is hidden — not disabled-with-a-tooltip — for staff
 * without it, matching how "Send Portal Access" already behaves on this page:
 * do not offer an action that will fail at the server.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
	fetchInvitesForStudent,
	revokePortalInvite,
	inviteKindLabel,
	isExpired,
	toDate,
	INVITE_PENDING,
	INVITE_CLAIMED,
	INVITE_REVOKED,
} from '../../services/portalInviteService';
import './PortalInvites.css';

const fmt = (value) => {
	const d = toDate(value);
	return d ? d.toLocaleDateString() : null;
};

/**
 * One badge per invite, and the ORDER of these checks is the meaning.
 * Expiry is only ever reported for a pending invite (see isExpired), so a
 * claimed invite past its expiry correctly reads "Claimed" — the account
 * exists, and calling it expired would be false.
 */
function statusBadge(invite) {
	if (invite.status === INVITE_CLAIMED) {
		return { cls: 'success', icon: 'fa-circle-check', label: 'Claimed' };
	}
	if (invite.status === INVITE_REVOKED) {
		return { cls: 'neutral', icon: 'fa-ban', label: 'Revoked' };
	}
	if (isExpired(invite)) {
		return { cls: 'warning', icon: 'fa-hourglass-end', label: 'Expired' };
	}
	return { cls: 'info', icon: 'fa-paper-plane', label: 'Pending' };
}

// Only a live pending invite is worth revoking. A revoked one is already dead
// (the callable would no-op), a claimed one throws by design, and an expired
// one can no longer be redeemed — offering Revoke there would imply the link
// was still dangerous when it is not.
export function canRevoke(invite) {
	return invite.status === INVITE_PENDING && !isExpired(invite);
}

const PortalInvites = ({ studentId, familyId, canManage = false, invitesOverride }) => {
	// `invitesOverride` is the same probe seam WorkTasksModule's `tasksOverride`
	// provides: with it the component renders real markup against injected rows,
	// with NO auth and NO Firestore. jsdom computes no layout, so the row's
	// squeeze behaviour cannot be asserted in a test — it has to be looked at,
	// and this is what makes looking at it possible.
	const injected = Array.isArray(invitesOverride);
	const [invites, setInvites] = useState(injected ? invitesOverride : []);
	const [loading, setLoading] = useState(!injected);
	const [error, setError] = useState('');
	const [busyId, setBusyId] = useState('');
	const [notice, setNotice] = useState('');

	const load = useCallback(async () => {
		try {
			setError('');
			const rows = await fetchInvitesForStudent({ studentId, familyId });
			setInvites(rows);
		} catch (e) {
			// A denied read is the likely cause and it is worth naming, rather
			// than rendering an empty list that reads as "never invited".
			setError('Could not load portal invites.');
			console.error('PortalInvites load failed:', e);
		} finally {
			setLoading(false);
		}
	}, [studentId, familyId]);

	useEffect(() => {
		if (injected) return undefined;
		let alive = true;
		setLoading(true);
		load().finally(() => {
			if (!alive) return;
		});
		return () => { alive = false; };
	}, [load, injected]);

	const handleRevoke = async (invite) => {
		const who = invite.recipientEmail || 'this recipient';
		// eslint-disable-next-line no-alert
		if (!window.confirm(
			`Revoke the portal invite sent to ${who}?\n\n` +
			'The link stops working immediately. They can be invited again at any time.'
		)) return;

		setBusyId(invite.id);
		setNotice('');
		setError('');
		try {
			const res = await revokePortalInvite(invite.id);
			setNotice(res?.alreadyRevoked
				? 'That invite had already been revoked.'
				: `Invite to ${who} revoked.`);
			await load();
		} catch (e) {
			// The claimed case is a real, expected outcome — the server refuses
			// because revoking cannot un-create an account. Surface its message
			// rather than a generic failure, since it tells staff what to do.
			setError(e?.message || 'Could not revoke that invite.');
			console.error('revokePortalInvite failed:', e);
		} finally {
			setBusyId('');
		}
	};

	return (
		<div className="ps-card">
			<h2 className="ps-card__band">Portal Access</h2>

			{loading && <p className="text-muted mb-0">Loading invites…</p>}

			{!loading && error && (
				<div className="ps-alert ps-alert--warning">
					<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
					{error}
				</div>
			)}

			{!loading && notice && (
				<div className="ps-alert ps-alert--info">
					<i className="fa-sharp fa-solid fa-circle-info me-2" />
					{notice}
				</div>
			)}

			{!loading && !error && invites.length === 0 && (
				<p className="text-muted mb-0">
					No portal invite has been sent for this student.
				</p>
			)}

			{!loading && invites.length > 0 && (
				<ul className="pi-list">
					{invites.map((invite) => {
						const badge = statusBadge(invite);
						const sent = fmt(invite.sentAt) || fmt(invite.createdAt);
						const expires = fmt(invite.expiresAt);
						return (
							<li key={invite.id} className="pi-row">
								<div className="pi-row__main">
									<div className="pi-row__top">
										<span className="pi-row__kind">{inviteKindLabel(invite)}</span>
										<span className={`ps-badge ps-badge--${badge.cls}`}>
											<i className={`fa-sharp fa-solid ${badge.icon} me-1`} />
											{badge.label}
										</span>
									</div>
									<div className="pi-row__email">
										{invite.recipientEmail || <span className="text-muted">no email recorded</span>}
									</div>
									<div className="pi-row__meta text-muted">
										{sent ? `Sent ${sent}` : 'Send date not recorded'}
										{invite.status === INVITE_CLAIMED && fmt(invite.claimedAt) &&
											` · Claimed ${fmt(invite.claimedAt)}`}
										{invite.status === INVITE_PENDING && expires &&
											` · ${isExpired(invite) ? 'Expired' : 'Expires'} ${expires}`}
										{invite.status === INVITE_REVOKED && fmt(invite.revokedAt) &&
											` · Revoked ${fmt(invite.revokedAt)}`}
									</div>
								</div>
								{canManage && canRevoke(invite) && (
									<button
										type="button"
										className="btn-cyv-ghost pi-row__action"
										onClick={() => handleRevoke(invite)}
										disabled={busyId === invite.id}
										title="Revoke this invite — the link stops working immediately">
										<i className="fa-sharp fa-solid fa-ban" />{' '}
										{busyId === invite.id ? 'Revoking…' : 'Revoke'}
									</button>
								)}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default PortalInvites;
